import React, { createContext, JSX, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { formatDataForAnalytics, trackFunnelStep } from '@workspace/utils';
import type { ContentItemProps, FormConfig, StateResolver } from '../types';
import { DefaultStateResolver, StepProps } from '../utils';

export interface MetaState {
    source?: string;
    step: number;
    subStep: number;
    totalSubSteps: number;
    token: string;
    key?: string;
}

export interface Actions {
    updateStep: (step: number) => void;
    updateToken: (token: string) => void;
    saveAndGoNextStep: (data: any) => void;
    goNextQuestion: (data?: any) => void;
    goPreviousQuestion: () => void;
    goToStep: (slug: string) => void;
    saveInitialData: (data: any) => void | Promise<void>;
    saveData: (data: any) => void;
    /** Met à jour les données de session (/init, /fill, /save_devis…) sans toucher à l'état du formulaire. */
    updateOthers: (data: any) => void;
    /** Remplace l'état du formulaire sans navigation (ex. restauration de session en développement). */
    replaceState: (data: any) => void;
    getContent: (content: ContentItemProps) => string | string[] | JSX.Element | JSX.Element[];
    setReturnToStep: (slug: string) => void;
    clearReturnToStep: () => void;
}

export interface GlobalFormContextType {
    origin?: string;
    others?: any;
    state: any;
    initialState: any;
    meta: MetaState;
    setMeta: React.Dispatch<React.SetStateAction<MetaState>>;
    disableNavigation: boolean;
    setDisableNavigation: React.Dispatch<React.SetStateAction<boolean>>;
    actions: Actions;
    steps: StepProps[];
    config?: FormConfig;
}

export const initialMetaState = {
    step: 0,
    subStep: 0,
    token: '',
    key: '',
} as MetaState;

const GlobalFormContext = createContext<GlobalFormContextType | undefined>({
    origin: 'direct', // default value
    steps: [],
    state: {} as any,
    initialState: {} as any,
    meta: initialMetaState,
    setMeta: () => {
        throw new Error('setMeta function must be overrided');
    },
    disableNavigation: false,
    setDisableNavigation: () => {
        throw new Error('setDisableNavigation function must be overrided');
    },
    actions: {
        updateStep: () => {
            throw new Error('updateStep function must be overrided');
        },
        updateToken: () => {
            throw new Error('updateToken function must be overrided');
        },
        saveAndGoNextStep: () => {
            throw new Error('saveAndGoNextStep function must be overrided');
        },
        goNextQuestion: () => {
            throw new Error('nextQuestion function must be overrided');
        },
        goPreviousQuestion: () => {
            throw new Error('previousQuestion function must be overrided');
        },
        goToStep: () => {
            throw new Error('goToStep function must be overrided');
        },
        saveInitialData: () => {
            throw new Error('saveInitialData function must be overrided');
        },
        saveData: () => {
            throw new Error('saveData function must be overrided');
        },
        updateOthers: () => {
            throw new Error('updateOthers function must be overrided');
        },
        replaceState: () => {
            throw new Error('replaceState function must be overrided');
        },
        getContent: () => {
            throw new Error('getContent function must be overrided');
        },
        setReturnToStep: () => {
            throw new Error('setReturnToStep function must be overrided');
        },
        clearReturnToStep: () => {
            throw new Error('clearReturnToStep function must be overrided');
        },
    },
});

export const useGlobalForm = () => {
    const context = useContext(GlobalFormContext);
    if (context === undefined) {
        throw new Error('useGlobalForm must be used within a GlobalFormProvider');
    }
    return context;
};

export interface GlobalFormProviderProps {
    children: React.ReactNode;
    steps: StepProps[];
    initialFormState: any;
    origin?: string;
    stateResolver?: StateResolver; // Nouveau prop pour le resolver
    config?: FormConfig; // Configuration optionnelle du formulaire
}

export const GlobalFormProvider: React.FC<GlobalFormProviderProps> = ({
    children,
    steps,
    initialFormState,
    origin,
    stateResolver = new DefaultStateResolver(), // Utilise le resolver par défaut si non fourni
    config,
}) => {
    const navigate = useNavigate();

    const [state, setState] = useState<any>(null);
    const [initialState, setInitialState] = useState<any>(null);
    const [meta, setMeta] = useState<MetaState>(initialMetaState);
    const [history, setHistory] = useState<{ step: number; subStep: number }[]>([]);
    const [others, setOthers] = useState<any>({});

    const getFilteredSteps = useCallback(
        (_initialState = initialState, _steps = steps, _state = state) => {
            const source = _initialState?.source;

            if (!source) return _steps;

            // if source is provided, check if the step has a source attribute and if it matches with the source
            const temp = _steps
                .filter((step) => !step.source || step.source.includes(source))
                .filter((step) => !step.showWhen || step.showWhen(_state, _initialState))
                .filter((step) => {
                    if (_initialState?.DemandeTarif?.ActeGestion === 'AV') {
                        const motifMatch = (step.isAvenant ?? true) && (step.avenantMotifs?.includes(_state?.DemandeTarif?.Police?.MotifAvenant) ?? true);

                        const interlocuteurMatch = !step.avenantInterlocuteurs || step.avenantInterlocuteurs.includes(_state?.Interlocuteur);

                        return motifMatch && interlocuteurMatch;
                    }

                    if (_initialState?.DemandeTarif?.ActeGestion === 'AN') {
                        return step.isAffaireNouvelle ?? true;
                    }

                    return true;
                });

            return temp;
        },
        [initialState, steps, state],
    );

    const [disableNavigation, setDisableNavigation] = useState(false);

    const updateHistory = useCallback(
        (newStep: number, newSubStep: number) => {
            setHistory((prevHistory) => {
                const index = prevHistory.findIndex((h) => h.step === newStep && h.subStep === newSubStep);

                if (index !== -1) {
                    // Si l'étape est déjà dans l'historique, supprimer tout ce qui est après
                    return prevHistory.slice(0, index + 1);
                }
                // Trouver si nous faisons un saut en arrière vers une nouvelle étape
                const futureStepIndex = prevHistory.findIndex((h) => h.step > newStep);
                if (futureStepIndex !== -1) {
                    // Si c'est un saut en arrière, supprimer l'historique "futur"
                    return [...prevHistory.slice(0, futureStepIndex), { step: newStep, subStep: newSubStep }];
                }
                // Sinon, ajouter la nouvelle étape/sub-étape à la suite

                return [...prevHistory, { step: newStep, subStep: newSubStep }];
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [history],
    );

    const updateToken = useCallback(
        (token: string) => {
            const validToken = z.string().uuid().safeParse(token);

            if (!validToken.success) {
                throw new Error(validToken.error.issues[0].message);
            }

            setMeta((prev) => ({ ...prev, token }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [meta.token],
    );

    const updateStep = useCallback(
        (step: number, withNavigate = true, _initialState = initialState) => {
            const validStep = z.number().int().min(0).safeParse(step);

            if (!validStep.success) {
                throw new Error(validStep.error.issues[0].message);
            }

            const _filteredSteps = getFilteredSteps(_initialState);

            const maxIndex = Math.max(..._filteredSteps.map((s) => s.index));
            if (step > maxIndex) {
                throw new Error('Step out of range');
            }

            const stepData = _filteredSteps.find((s) => s.index === step) as StepProps;

            if (withNavigate) navigate(stepData?.path);
            setMeta((prev) => ({ ...prev, step, subStep: 0 }));
            updateHistory(step, 0); // Assurez-vous d'appeler updateHistory ici pour ajuster l'historique
            window.scrollTo({ top: 0, behavior: 'instant' });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [meta.step, updateHistory, navigate, getFilteredSteps()],
    );

    const goNextQuestion = useCallback(
        (data: any = state) => {
            // Si returnToStep est défini, y retourner au lieu d'avancer normalement
            if (returnToStepRef.current) {
                const returnSlug = returnToStepRef.current;
                returnToStepRef.current = null;
                goToStep(returnSlug);
                return;
            }

            const _steps = getFilteredSteps(initialState, steps, data);

            // Assumer que chaque question a un attribut `data-active` qui indique si elle est active.
            const questions = Array.from(document.querySelectorAll('.step-question'));
            const nextSubStepIndex = questions.findIndex((el, index) => index > meta.subStep && el.getAttribute('data-active') === 'true');
            // find max "index" in the filteredSteps
            const totalSteps = Math.max(..._steps.map((s) => s.index)) + 1;

            // Si une question active est trouvée dans les sous-étapes actuelles.
            if (nextSubStepIndex !== -1) {
                setMeta((prevMeta) => ({ ...prevMeta, subStep: nextSubStepIndex }));
                updateHistory(meta.step, nextSubStepIndex);
            } else {
                // Si aucune question active n'est trouvée, passer à l'étape suivante.
                const nextStep = _steps.filter((s: { index: number }) => s.index > meta.step)[0] as StepProps;
                const nextStepIndex = nextStep?.index ?? meta.step + 1;
                if (nextStepIndex < totalSteps) {
                    setMeta((prevMeta) => ({ ...prevMeta, step: nextStepIndex, subStep: 0 }));
                    updateHistory(nextStepIndex, 0);
                    navigate(nextStep.path);
                } else {
                    // Si aucune étape active suivante n'est trouvée.
                    console.debug('Fin du formulaire ou aucune question active trouvée dans les étapes restantes.');
                }
            }
            window.scrollTo({ top: 0, behavior: 'instant' });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [meta, getFilteredSteps(), updateHistory, navigate],
    );

    const goPreviousQuestion = useCallback(() => {
        if (history.length > 1) {
            // Retour à l'entrée précédente de l'historique
            let newHistory = history.slice(0, -1);
            // why: dans l'étape courante, une entrée peut pointer une question inactive (question conditionnelle, ou
            // sous-étape 0 corrigée au montage) : l'afficher donnerait une page vide. On prend la première question active
            // entre cette entrée et la question courante, sinon on remonte d'une entrée.
            const questions = Array.from(document.querySelectorAll('.step-question'));
            const isActive = (index: number) => questions[index]?.getAttribute('data-active') === 'true';
            while (newHistory.length > 0) {
                const last = newHistory[newHistory.length - 1];
                if (last.step !== meta.step || isActive(last.subStep)) break;
                const active = questions.findIndex((_, index) => index >= last.subStep && index < meta.subStep && isActive(index));
                if (active !== -1) {
                    newHistory = [...newHistory.slice(0, -1), { step: last.step, subStep: active }];
                    break;
                }
                newHistory = newHistory.slice(0, -1);
            }
            const previousPosition = newHistory[newHistory.length - 1];
            if (!previousPosition) return;

            setHistory(newHistory);
            setMeta({ ...meta, step: previousPosition.step, subStep: previousPosition.subStep });
            const previousStep = getFilteredSteps().filter((s: { index: number }) => s.index === previousPosition.step)[0] as StepProps;
            navigate(previousStep.path);
        } else if (meta.step > 0 || meta.subStep >= 0) {
            // Cas où il n'y a pas d'historique suffisant, mais un recul est possible
            // Cette situation peut nécessiter une gestion spécifique, comme un retour à une étape/sub-étape par défaut
            const actualIndexInArray = getFilteredSteps().findIndex((s) => s.index === meta.step);
            const previousStep = getFilteredSteps()[actualIndexInArray - 1];

            // console.log('previousStep', previousStep);
            setHistory([{ step: previousStep.index, subStep: 0 }]);
            setMeta({ ...meta, step: previousStep.index, subStep: 0 });
            navigate(previousStep.path);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, meta, setHistory, setMeta, navigate, getFilteredSteps()]);

    const saveAndGoNextStep = (data: any) => {
        setState(data);
        goNextQuestion(data);
    };

    // why: initialFormState peut être asynchrone (ex. avenant : rechargement du véhicule wheels
    // avant de construire l'état) et reçoit la réponse /init complète en second argument
    // (state, apporteurs…). Les apps existantes, synchrones à un argument, ne sont pas impactées.
    const saveInitialData = async (data: any) => {
        const formData = data?.data;
        const finalForm = await initialFormState(formData, data);

        setInitialState(finalForm);
        setState(finalForm);
        setMeta((prev) => ({ ...prev, token: data.token }));
        setOthers({ ...data, data: undefined, initData: formData });

        const isAvenant = finalForm.DemandeTarif?.ActeGestion === 'AV';
        const source = data?.data.source;

        // Utilisation du StateResolver pour déterminer l'étape appropriée
        const possibleStep = stateResolver.resolveStepFromState(data.state, {
            isAvenant,
            finalForm,
            source,
            steps,
            data,
        });

        if (possibleStep) {
            updateStep(possibleStep.index, true, finalForm);
        }

        // Gestion du whitelabel (reste inchangé)
        const whitelabel = data?.whitelabel;

        if (data?.source !== 'site' && data?.apporteur?.codeBelair !== 43397) {
            const colors = [
                'style_color1',
                'style_color2',
                // 'style_color3',
                // 'style_color4',
                // 'style_color5',
            ];

            colors.forEach((color) => {
                document.documentElement.style.setProperty(`--${color}`, whitelabel[color]);
            });

            document.documentElement.style.setProperty('--style_color4', whitelabel.style_color1 + '08');
            document.documentElement.style.setProperty('--style_color5', whitelabel.style_color1 + '08');
        }

        // setCookie('devis_token', data.token, { expires: 1 / 24, sameSite: 'None', secure: false });
        localStorage.setItem('devis_token', data.token);
    };

    /**
     * if not source is provided, we default to site
     * if concessionnaire is provided and content not found, we default to site
     * if conseiller is provided and content not found, we default to courtier
     */
    const getContent = useCallback(
        (content: ContentItemProps) => {
            let temp;

            switch (initialState?.source) {
                case 'concessionnaire':
                    temp = content.concessionnaire ?? content.site;
                    break;
                case 'conseiller':
                    temp = content.conseiller ?? content.courtier;
                    break;
                case 'courtier':
                    temp = content.courtier ?? content.conseiller;
                    break;
                default:
                    temp = content.site;
            }

            // if String, return
            // if String[], return with .map(text, key)
            // if JSX.Element, return
            // if JSX.Element[], return with .map(element, key)

            if (typeof temp === 'string') {
                return <>{temp}</>; // Wrap string in a paragraph for consistency
            } else if (Array.isArray(temp)) {
                if (typeof temp[0] === 'string') {
                    // Handling String[]
                    return temp.map((text, key) => <p key={key}>{text}</p>);
                }
                // Assuming temp is JSX.Element[] if not string[]
                // Ensure each element has a unique key
                return temp.map((element, key) => React.cloneElement(element as JSX.Element, { key }));
            }
            // Assuming temp is JSX.Element if not string or string[]
            return temp;
        },
        [initialState],
    );

    const saveData = (data: any) => {
        const formData = data?.data;

        setState(formData);
        setOthers({ ...data, data: undefined, initData: formData });
    };

    const replaceState = (data: any) => {
        setState(data);
    };

    const updateOthers = (data: any) => {
        setOthers({ ...data, data: undefined, initData: data?.data });
    };

    const goToStep = useCallback(
        (slug: string) => {
            const _filteredSteps = getFilteredSteps();
            const targetStep = _filteredSteps.find((s) => s.slug === slug);

            if (!targetStep) {
                console.error(`Step with slug "${slug}" not found`);
                return;
            }

            // Navigate to the step
            navigate(targetStep.path);
            setMeta((prev) => ({ ...prev, step: targetStep.index, subStep: 0 }));
            updateHistory(targetStep.index, 0);
            window.scrollTo({ top: 0, behavior: 'instant' });
        },
        [getFilteredSteps, navigate, updateHistory],
    );

    const returnToStepRef = useRef<string | null>(null);

    const setReturnToStep = useCallback((slug: string) => {
        returnToStepRef.current = slug;
    }, []);

    const clearReturnToStep = useCallback(() => {
        returnToStepRef.current = null;
    }, []);

    // Analytics: track funnelStepLoad when step changes
    const prevStepRef = useRef<number | null>(null);
    useEffect(() => {
        if (
            config?.features?.enableAnalytics &&
            config?.analytics?.formType &&
            state &&
            meta.step !== prevStepRef.current
        ) {
            const apporteur = others?.apporteur?.codeBelair;
            const stepData = getFilteredSteps().find((s) => s.index === meta.step);
            const stepName = stepData?.title ?? `Step ${meta.step}`;

            const formattedData = formatDataForAnalytics(config.analytics.formType, state);
            trackFunnelStep(stepName, formattedData, apporteur);

            prevStepRef.current = meta.step;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta.step, state, config, others]);

    const actions = {
        updateToken,
        updateStep,
        saveAndGoNextStep,
        goNextQuestion,
        goPreviousQuestion,
        goToStep,
        saveInitialData,
        saveData,
        updateOthers,
        replaceState,
        getContent,
        setReturnToStep,
        clearReturnToStep,
    };

    const value = {
        origin,
        others,
        state,
        initialState,
        meta,
        setMeta,
        disableNavigation,
        setDisableNavigation,
        actions,
        steps: getFilteredSteps(),
        config,
    };

    return <GlobalFormContext.Provider value={value}>{children}</GlobalFormContext.Provider>;
};
